import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { Icon, IconName } from './Icon'
import { Link } from './Link'
import { Color } from '../constants/color'
import { getIntentProps } from '../constants/intent'
import { theme } from '../constants/theme'
import { StyleProps, createStyles } from '../utils/create-styles'
import { SpaceProps, space } from '../utils/styled-system'

type ColorStyles = {
  backgroundColor: Color
  borderColor?: Color
  color: Color
  fontWeight?: number
}

type SizeStyles = {
  fontSize: number
  paddingX: number
  paddingY: number
}

const COLOR_STYLE_MAP: Record<BadgeColor, ColorStyles> = {
  blue: {
    backgroundColor: Color.LIGHTEST_BLUE,
    color: Color.DARK_BLUE,
  },
  danger: {
    backgroundColor: getIntentProps('danger').backgroundColor,
    color: getIntentProps('danger').color,
  },
  'dark-blue': {
    backgroundColor: Color.DARK_BLUE,
    color: Color.WHITE,
  },
  'dark-green': {
    backgroundColor: Color.DARK_GREEN,
    color: Color.WHITE,
  },
  'dark-red': {
    backgroundColor: Color.DARK_RED,
    color: Color.WHITE,
  },
  'darker-green': {
    backgroundColor: Color.TRANSPARENT_GREEN,
    color: Color.DARKER_GREEN,
  },
  'lighter-green': {
    backgroundColor: Color.LIGHTER_GREEN,
    color: Color.DARK_GREY,
  },
  green: {
    backgroundColor: Color.TRANSPARENT_GREEN,
    color: Color.DARK_GREEN,
  },
  grey: {
    backgroundColor: Color.GREY,
    color: Color.DARK_GREY,
  },
  'light-blue': {
    backgroundColor: Color.LIGHTER_BLUE,
    color: Color.DARK_BLUE,
  },
  'medium-green': {
    backgroundColor: Color.GREEN,
    color: Color.WHITE,
  },
  'light-yellow': {
    backgroundColor: Color.LIGHT_YELLOW,
    color: Color.DARK_YELLOW,
  },
  violet: {
    backgroundColor: Color.TRANSPARENT_PURPLE,
    color: Color.VIOLET,
  },
  purple: {
    backgroundColor: Color.TRANSPARENT_PURPLE,
    color: Color.DEEP_PURPLE,
  },
  red: {
    backgroundColor: Color.LIGHTER_RED,
    color: Color.DARK_RED,
  },
  yellow: {
    backgroundColor: Color.YELLOW,
    color: Color.DARK_YELLOW,
  },
  white: {
    backgroundColor: Color.WHITE,
    borderColor: theme.color.border,
    color: Color.BLACK,
  },
  transparent: {
    backgroundColor: Color.TRANSPARENT,
    color: Color.DARK_GREY,
    borderColor: Color.MEDIUM_GREY,
    fontWeight: theme.font.weight.book,
  },
  'transparent-purple': {
    backgroundColor: Color.TRANSPARENT_PURPLE,
    color: Color.DARK_GREY,
  },
  'lighter-blue': {
    backgroundColor: Color.LIGHTER_BLUE,
    color: Color.DARK_GREY,
  },
  'golden-rod': {
    backgroundColor: Color.GOLDEN_ROD,
    color: Color.WHITE,
  },
  orange: {
    backgroundColor: Color.ORANGE,
    color: Color.CHARCOAL,
  },
  error: {
    backgroundColor: Color.WHITE,
    borderColor: Color.RED,
    color: Color.RED,
  },
}

const SIZE_STYLE_MAP: Record<BadgeSize, SizeStyles> = {
  xxxs: {
    fontSize: 8,
    paddingX: 3,
    paddingY: 1,
  },
  xxs: {
    fontSize: 12,
    paddingX: 4,
    paddingY: 0,
  },
  xs: {
    fontSize: theme.font.size[6.5],
    paddingX: 6,
    paddingY: 0,
  },
  sm: {
    fontSize: theme.font.size[6],
    paddingX: 6,
    paddingY: 1,
  },
  md: {
    fontSize: theme.font.size[5],
    paddingX: 8,
    paddingY: 1,
  },
}

const BadgeElement = styled.div<BadgeProps>((props) => {
  const { backgroundColor, color, borderColor, fontWeight } =
    COLOR_STYLE_MAP[props.color ?? 'grey']
  const { fontSize, paddingX, paddingY } = SIZE_STYLE_MAP[props.size ?? 'md']

  return {
    alignItems: 'center',
    backgroundColor,
    border: `${theme.border.width.thin}px solid ${
      borderColor ?? backgroundColor
    }`,
    borderRadius: props.pill
      ? 100
      : props.squared
        ? theme.border.radius.normal
        : 12,
    color,
    cursor: props.cursor,
    display: 'inline-flex',
    fontSize,
    fontWeight: fontWeight ?? theme.font.weight.bold,
    gridGap: 6,
    letterSpacing: props.uppercase ? '0.05rem' : undefined,
    lineHeight: 1.3,
    paddingBottom: paddingY,
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    textAlign: 'center',
    textDecoration: 'none',
    textTransform: props.uppercase ? 'uppercase' : undefined,
    userSelect: 'none',
    ...(borderColor && { borderColor }),
    ...space(props),
    ...createStyles(props.styles),
  }
})

export type BadgeColor =
  | 'blue'
  | 'danger'
  | 'dark-blue'
  | 'dark-green'
  | 'dark-red'
  | 'darker-green'
  | 'green'
  | 'grey'
  | 'lighter-green'
  | 'lighter-blue'
  | 'light-blue'
  | 'light-yellow'
  | 'medium-green'
  | 'purple'
  | 'red'
  | 'yellow'
  | 'white'
  | 'transparent'
  | 'violet'
  | 'transparent-purple'
  | 'golden-rod'
  | 'orange'
  | 'error'

export type BadgeProps = SpaceProps & {
  children?: ReactNode
  color?: BadgeColor
  cursor?: string
  icon?: IconName
  iconPosition?: 'left' | 'right'
  pill?: boolean
  size?: BadgeSize
  squared?: boolean
  styles?: StyleProps
  title?: string
  to?: string
  uppercase?: boolean
  onClick?: () => void
}

export type BadgeSize = 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md'

export const Badge = (props: BadgeProps) => {
  const isRight = props.iconPosition === 'right'

  const badgeElement = (
    <BadgeElement {...props} size={props.size}>
      {props.icon && !isRight && <Icon name={props.icon} size="sm" />}
      {props.children}
      {props.icon && isRight && <Icon name={props.icon} size="sm" />}
    </BadgeElement>
  )

  return props.to ? (
    <Link styles={{ display: 'inherit' }} to={props.to}>
      {badgeElement}
    </Link>
  ) : (
    badgeElement
  )
}
